import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { affiliateLink } from "../../lib/urls";
import { toast } from "sonner";
import {
    Gift, Users, Store, Wallet, Coins, RotateCcw, Settings2,
    LayoutDashboard, TrendingDown, Save, ShieldAlert, Search,
    Check, Ban, Eye, ChevronLeft, ChevronRight, Download, Megaphone, Plus, Power, Copy,
    X, MousePointerClick, Link2, CircleDollarSign,
} from "lucide-react";

const TABS = [
    { key: "overview", label: "Visão Geral", icon: LayoutDashboard },
    { key: "producers", label: "Produtor → Produtor", icon: Store },
    { key: "customers", label: "Cliente → Cliente", icon: Users },
    { key: "affiliates", label: "Afiliados Profissionais", icon: Megaphone },
    { key: "wallets", label: "Carteira Caseirinho", icon: Wallet },
    { key: "commissions", label: "Comissões", icon: Coins },
    { key: "refunds", label: "Reembolsos", icon: RotateCcw },
    { key: "fraud", label: "Fraude & Auditoria", icon: ShieldAlert },
    { key: "settings", label: "Configurações", icon: Settings2 },
];

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("pt-PT"); } catch { return "—"; } };

export default function AdminReferrals() {
    const [tab, setTab] = useState("overview");
    return (
        <section className="space-y-6" data-testid="admin-referrals">
            <div>
                <span className="label-overline text-wine/60 inline-flex items-center gap-1.5"><Gift className="h-3 w-3" /> Crescimento</span>
                <h1 className="font-heading text-3xl text-wine-dark">Afiliados & Recompensas</h1>
                <p className="text-sm text-wine-dark/60 mt-1">Gestão dos programas de indicação, carteira e comissões promocionais.</p>
            </div>

            <div className="flex flex-wrap gap-1 bg-cream rounded-md p-1 w-fit">
                {TABS.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`inline-flex items-center gap-2 label-overline text-[10px] px-3 py-2 rounded-md transition-colors ${tab === key ? "bg-wine text-cream" : "text-wine-dark hover:bg-wine/5"}`}
                        data-testid={`rewards-tab-${key}`}
                    >
                        <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                ))}
            </div>

            {tab === "overview" && <Overview />}
            {tab === "producers" && <Producers />}
            {tab === "customers" && <Customers />}
            {tab === "affiliates" && <Affiliates />}
            {tab === "wallets" && <Wallets />}
            {tab === "commissions" && <Commissions />}
            {tab === "refunds" && <Refunds />}
            {tab === "fraud" && <Fraud />}
            {tab === "settings" && <SettingsTab />}
        </section>
    );
}

function Kpi({ label, value, icon: Icon, highlight }) {
    return (
        <div className={`p-4 rounded-md border ${highlight ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-center justify-between">
                <span className={`label-overline text-[9px] ${highlight ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                {Icon && <Icon className={`h-4 w-4 ${highlight ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />}
            </div>
            <p className="font-heading text-2xl mt-1">{value}</p>
        </div>
    );
}

function Overview() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/rewards/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <Loading />;
    return (
        <div className="space-y-5">
            <h3 className="label-overline text-wine">Produtor → Produtor</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Produtores indicados" value={d.producers_indicated} icon={Store} />
                <Kpi label="Produtores qualificados" value={d.producers_qualified} icon={Store} />
                <Kpi label="Dias de benefício concedidos" value={d.benefit_days_granted} icon={TrendingDown} />
                <Kpi label="Economia p/ produtores" value={`€${d.savings.toFixed(2)}`} icon={Coins} highlight />
            </div>
            <h3 className="label-overline text-wine">Cliente → Cliente & Carteira</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Clientes indicados" value={d.customers_invited} icon={Users} />
                <Kpi label="Compras qualificadas" value={d.customer_purchases_qualified} icon={Users} />
                <Kpi label="Créditos emitidos" value={`€${d.credits_issued.toFixed(2)}`} icon={Gift} />
                <Kpi label="Créditos utilizados" value={`€${d.credits_used.toFixed(2)}`} icon={Wallet} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Saldo total das carteiras" value={`€${d.wallets_total.toFixed(2)}`} icon={Wallet} highlight />
            </div>
        </div>
    );
}

const PROD_STATUS = {
    pending: "Pendente", awaiting_sale: "Aguardando venda", approved: "Aprovado",
    qualified: "Qualificado", rejected: "Rejeitado", reversed: "Revertido",
};

function Producers() {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/admin/rewards/producers").then((r) => setRows(r.data)).catch(() => {}); }, []);
    if (!rows) return <Loading />;
    return (
        <TableCard testid="rewards-producers" head={["Indicador", "Indicado", "Estado", "1ª venda", "Qualificação", "Dias restantes", "Comissão atual"]}>
            {rows.map((r) => (
                <tr key={r.id} className="border-t border-wine-dark/5">
                    <Td>{r.referrer_name}</Td>
                    <Td>{r.referred_name}</Td>
                    <Td><Badge label={PROD_STATUS[r.status] || r.status} status={r.status} /></Td>
                    <Td>{r.first_sale_order_id ? `#${r.first_sale_order_id.slice(0, 8)}` : "—"}</Td>
                    <Td>{r.qualified_at ? fmtDate(r.qualified_at) : "—"}</Td>
                    <Td>{r.remaining_days > 0 ? `${r.remaining_days} dias` : "—"}</Td>
                    <Td>{r.current_fee != null ? `${r.current_fee}%` : "—"}</Td>
                </tr>
            ))}
            {rows.length === 0 && <EmptyRow cols={7} />}
        </TableCard>
    );
}

function Customers() {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/admin/rewards/customers").then((r) => setRows(r.data)).catch(() => {}); }, []);
    if (!rows) return <Loading />;
    return (
        <TableCard testid="rewards-customers" head={["Indicador", "Cliente convidado", "Estado", "1ª compra", "Desconto amigo", "Crédito gerado", "Data"]}>
            {rows.map((r) => (
                <tr key={r.id} className="border-t border-wine-dark/5">
                    <Td>{r.referrer_name}</Td>
                    <Td>{r.friend_name}</Td>
                    <Td><Badge label={PROD_STATUS[r.status] || r.status} status={r.status} /></Td>
                    <Td>{r.first_sale_order_id ? `#${r.first_sale_order_id.slice(0, 8)}` : "—"}</Td>
                    <Td>{r.discount_granted ? `€${r.discount_granted.toFixed(2)}` : "—"}</Td>
                    <Td className="text-vineyard">{r.credit_generated ? `€${r.credit_generated.toFixed(2)}` : "—"}</Td>
                    <Td>{fmtDate(r.qualified_at || r.created_at)}</Td>
                </tr>
            ))}
            {rows.length === 0 && <EmptyRow cols={7} />}
        </TableCard>
    );
}

function Wallets() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/rewards/wallets").then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <Loading />;
    return (
        <div className="space-y-6">
            <TableCard title="Saldos das carteiras" testid="rewards-wallets" head={["Cliente", "Email", "Saldo"]}>
                {d.wallets.filter((w) => w.balance !== 0).map((w) => (
                    <tr key={w.user_id} className="border-t border-wine-dark/5">
                        <Td>{w.name}</Td><Td className="text-xs text-wine-dark/60">{w.email}</Td>
                        <Td className="font-heading text-vineyard">€{w.balance.toFixed(2)}</Td>
                    </tr>
                ))}
                {d.wallets.filter((w) => w.balance !== 0).length === 0 && <EmptyRow cols={3} />}
            </TableCard>
            <TableCard title="Ledger completo" testid="rewards-ledger" head={["Cliente", "Tipo", "Descrição", "Encomenda", "Validade", "Estado", "Valor"]}>
                {d.ledger.map((t) => (
                    <tr key={t.id} className="border-t border-wine-dark/5">
                        <Td>{t.user_name}</Td>
                        <Td>{t.kind === "credit" ? "Crédito" : "Débito"}</Td>
                        <Td className="text-xs">{t.description || "—"}</Td>
                        <Td>{t.order_id ? `#${t.order_id.slice(0, 8)}` : "—"}</Td>
                        <Td className="text-xs">{t.expires_at ? fmtDate(t.expires_at) : "—"}</Td>
                        <Td><Badge label={t.status} status={t.status} /></Td>
                        <Td className={t.kind === "credit" ? "text-vineyard" : "text-wine"}>{t.kind === "credit" ? "+" : "−"}€{t.amount.toFixed(2)}</Td>
                    </tr>
                ))}
                {d.ledger.length === 0 && <EmptyRow cols={7} />}
            </TableCard>
        </div>
    );
}

function Commissions() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/rewards/commissions").then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <Loading />;
    return (
        <div className="space-y-6">
            <TableCard title="Benefícios ativos" testid="rewards-active-benefits" head={["Produtor", "Comissão promo", "Comissão normal", "Dias restantes", "Fim do benefício"]}>
                {d.active.map((a) => (
                    <tr key={a.producer_id} className="border-t border-wine-dark/5">
                        <Td>{a.producer_name}</Td>
                        <Td className="text-vineyard">{a.promo_fee}%</Td>
                        <Td>{a.standard_fee}%</Td>
                        <Td>{a.remaining_benefit_days} dias</Td>
                        <Td>{fmtDate(a.benefit_end)}</Td>
                    </tr>
                ))}
                {d.active.length === 0 && <EmptyRow cols={5} />}
            </TableCard>
            <TableCard title="Histórico de comissões" testid="rewards-commission-history" head={["Produtor", "Ação", "Dias adicionados", "Saldo (dias)", "Comissão promo", "Data"]}>
                {d.history.map((h) => (
                    <tr key={h.id} className="border-t border-wine-dark/5">
                        <Td>{h.producer_name}</Td>
                        <Td className="text-xs">{h.action}</Td>
                        <Td>+{h.added_days}</Td>
                        <Td>{h.remaining_after}</Td>
                        <Td className="text-vineyard">{h.promo_fee}%</Td>
                        <Td>{fmtDate(h.created_at)}</Td>
                    </tr>
                ))}
                {d.history.length === 0 && <EmptyRow cols={6} />}
            </TableCard>
        </div>
    );
}

function Refunds() {
    const [d, setD] = useState(null);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const load = () => {
        const p = new URLSearchParams({ page });
        if (q) p.set("q", q);
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        return api.get(`/admin/rewards/refunds?${p.toString()}`).then((r) => setD(r.data)).catch(() => {});
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
    if (!d) return <Loading />;
    async function refund(id) {
        if (!window.confirm("Reembolsar esta encomenda? Isto reverte os créditos de indicação relacionados.")) return;
        try {
            await api.post(`/admin/orders/${id}/refund`);
            toast.success("Encomenda reembolsada e recompensas revertidas");
            load();
        } catch { toast.error("Erro ao reembolsar"); }
    }
    return (
        <div className="space-y-6">
            <div className="bg-white border border-gold-soft/30 rounded-md p-3 flex flex-wrap items-end gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 text-wine-dark/40 ml-1" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar #encomenda ou cliente…" className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="refunds-search" />
                </div>
                <label className="text-xs text-wine-dark/60">De <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="ml-1 border border-wine-dark/15 rounded px-2 py-1 text-xs" data-testid="refunds-date-from" /></label>
                <label className="text-xs text-wine-dark/60">Até <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="ml-1 border border-wine-dark/15 rounded px-2 py-1 text-xs" data-testid="refunds-date-to" /></label>
                <button onClick={() => { setPage(1); load(); }} className="btn-wine text-xs px-4 py-2" data-testid="refunds-apply">Aplicar</button>
            </div>
            <TableCard title={`Encomendas pagas (${d.total})`} testid="rewards-recent-paid" head={["Encomenda", "Cliente", "Total", "Crédito usado", "Desc. amigo", "Ação"]}>
                {d.recent_paid.map((o) => (
                    <tr key={o.id} className="border-t border-wine-dark/5">
                        <Td>#{o.id.slice(0, 8)}</Td>
                        <Td>{o.customer_name}</Td>
                        <Td className="font-heading">€{(o.total || 0).toFixed(2)}</Td>
                        <Td>{o.wallet_applied ? `€${o.wallet_applied.toFixed(2)}` : "—"}</Td>
                        <Td>{o.friend_discount ? `€${o.friend_discount.toFixed(2)}` : "—"}</Td>
                        <Td>
                            <button onClick={() => refund(o.id)} className="btn-wine-outline text-[10px] px-3 py-1.5 inline-flex items-center gap-1.5" data-testid={`refund-${o.id}`}>
                                <RotateCcw className="h-3 w-3" /> Reembolsar
                            </button>
                        </Td>
                    </tr>
                ))}
                {d.recent_paid.length === 0 && <EmptyRow cols={6} />}
            </TableCard>
            <div className="flex items-center justify-between">
                <span className="text-xs text-wine-dark/60">Página {d.page} de {d.total_pages}</span>
                <div className="flex gap-2">
                    <button disabled={d.page <= 1} onClick={() => setPage((p) => p - 1)} className="btn-wine-outline text-xs px-3 py-1.5 disabled:opacity-40" data-testid="refunds-prev"><ChevronLeft className="h-3.5 w-3.5" /></button>
                    <button disabled={d.page >= d.total_pages} onClick={() => setPage((p) => p + 1)} className="btn-wine-outline text-xs px-3 py-1.5 disabled:opacity-40" data-testid="refunds-next"><ChevronRight className="h-3.5 w-3.5" /></button>
                </div>
            </div>
            <TableCard title="Encomendas reembolsadas" testid="rewards-refunded" head={["Encomenda", "Cliente", "Total", "Data reembolso"]}>
                {d.refunded.map((o) => (
                    <tr key={o.id} className="border-t border-wine-dark/5">
                        <Td>#{o.id.slice(0, 8)}</Td><Td>{o.customer_name}</Td>
                        <Td>€{(o.total || 0).toFixed(2)}</Td><Td>{fmtDate(o.refunded_at)}</Td>
                    </tr>
                ))}
                {d.refunded.length === 0 && <EmptyRow cols={4} />}
            </TableCard>
        </div>
    );
}

const FRAUD_LEVEL = {
    low: "bg-gold-soft/20 text-gold-deep", medium: "bg-wine/10 text-wine", high: "bg-destructive/15 text-destructive",
};
const FRAUD_STATUS = {
    open: "Aberto", reviewed: "Analisado", approved: "Aprovado", rejected: "Rejeitado", blocked: "Bloqueado",
};

function Fraud() {
    const [rows, setRows] = useState(null);
    const [level, setLevel] = useState("all");
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const load = () => {
        const p = new URLSearchParams();
        if (level !== "all") p.set("level", level);
        if (status !== "all") p.set("status", status);
        if (q) p.set("q", q);
        return api.get(`/admin/rewards/fraud?${p.toString()}`).then((r) => setRows(r.data)).catch(() => {});
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [level, status]);
    async function exportFile(format) {
        try {
            const p = new URLSearchParams({ format });
            if (level !== "all") p.set("level", level);
            if (status !== "all") p.set("status", status);
            if (q) p.set("q", q);
            const res = await api.get(`/admin/rewards/fraud/export?${p.toString()}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = format === "xlsx" ? "fraude_auditoria.xlsx" : "fraude_auditoria.csv";
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Exportação concluída");
        } catch { toast.error("Erro ao exportar"); }
    }
    async function resolve(id, action) {
        const notes = action === "approved" ? "" : window.prompt("Motivo (opcional):", "") ?? "";
        try {
            await api.post(`/admin/rewards/fraud/${id}/resolve`, { action, notes });
            toast.success("Alerta atualizado");
            load();
        } catch { toast.error("Erro ao atualizar alerta"); }
    }
    if (!rows) return <Loading />;
    return (
        <div className="space-y-4" data-testid="rewards-fraud">
            <div className="bg-white border border-gold-soft/30 rounded-md p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Search className="h-4 w-4 text-wine-dark/40 ml-1" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Pesquisar utilizador, motivo…" className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="fraud-search" />
                </div>
                <select value={level} onChange={(e) => setLevel(e.target.value)} className="text-xs uppercase tracking-wider bg-cream border border-wine-dark/10 rounded-md px-3 py-2" data-testid="fraud-level-filter">
                    <option value="all">Todos os níveis</option><option value="high">HIGH</option><option value="medium">MEDIUM</option><option value="low">LOW</option>
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs uppercase tracking-wider bg-cream border border-wine-dark/10 rounded-md px-3 py-2" data-testid="fraud-status-filter">
                    <option value="all">Todos os estados</option>{Object.entries(FRAUD_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <div className="flex gap-2">
                    <button onClick={() => exportFile("csv")} className="btn-wine-outline text-[10px] px-3 py-2 inline-flex items-center gap-1.5" data-testid="fraud-export-csv"><Download className="h-3.5 w-3.5" /> CSV</button>
                    <button onClick={() => exportFile("xlsx")} className="btn-wine text-[10px] px-3 py-2 inline-flex items-center gap-1.5" data-testid="fraud-export-xlsx"><Download className="h-3.5 w-3.5" /> Excel</button>
                </div>
            </div>
            <TableCard testid="fraud-table" head={["Nível", "Motivo", "Utilizadores", "Programa", "Encomenda", "Data", "Estado", "Ações"]}>
                {rows.map((f) => (
                    <tr key={f.id} className="border-t border-wine-dark/5" data-testid={`fraud-row-${f.id}`}>
                        <Td><span className={`label-overline px-2 py-1 rounded text-[10px] ${FRAUD_LEVEL[f.level]}`}>{f.level?.toUpperCase()}</span></Td>
                        <Td className="text-xs">{(f.reasons || [f.reason]).join("; ")}</Td>
                        <Td className="text-xs">{(f.user_names || []).join(", ") || "—"}</Td>
                        <Td className="text-xs">{f.program === "producer" ? "Produtor" : "Cliente"}</Td>
                        <Td>{f.order_id ? `#${f.order_id.slice(0, 8)}` : "—"}</Td>
                        <Td className="text-xs">{fmtDate(f.created_at)}</Td>
                        <Td><Badge label={FRAUD_STATUS[f.status] || f.status} status={f.status === "approved" ? "qualified" : f.status === "open" ? "pending" : "reversed"} /></Td>
                        <Td>
                            {f.status === "open" ? (
                                <div className="flex gap-1">
                                    <button onClick={() => resolve(f.id, "approved")} title="Aprovar recompensa" className="p-1.5 rounded border border-vineyard/30 text-vineyard hover:bg-vineyard/10" data-testid={`fraud-approve-${f.id}`}><Check className="h-3.5 w-3.5" /></button>
                                    <button onClick={() => resolve(f.id, "reviewed")} title="Marcar analisado" className="p-1.5 rounded border border-wine-dark/20 text-wine-dark hover:bg-wine/5" data-testid={`fraud-review-${f.id}`}><Eye className="h-3.5 w-3.5" /></button>
                                    <button onClick={() => resolve(f.id, "rejected")} title="Rejeitar" className="p-1.5 rounded border border-destructive/30 text-destructive hover:bg-destructive/10" data-testid={`fraud-reject-${f.id}`}><Ban className="h-3.5 w-3.5" /></button>
                                </div>
                            ) : <span className="text-xs text-wine-dark/40">{f.resolved_by_name || "—"}</span>}
                        </Td>
                    </tr>
                ))}
                {rows.length === 0 && <EmptyRow cols={8} />}
            </TableCard>
            <p className="text-xs text-wine-dark/50">Alertas HIGH retêm a recompensa até aprovação (configurável). Toda ação fica registada em auditoria e o histórico nunca é apagado.</p>
        </div>
    );
}

function SettingsTab() {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/admin/referral-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
    if (!s) return <Loading />;
    const setField = (prog, key, val) => setS((prev) => ({ ...prev, [prog]: { ...prev[prog], [key]: val } }));
    async function save() {
        setBusy(true);
        try {
            const { data } = await api.patch("/admin/referral-settings", { producer: s.producer, customer: s.customer, fraud: s.fraud });
            setS(data);
            toast.success("Configurações guardadas");
        } catch { toast.error("Erro ao guardar"); }
        setBusy(false);
    }
    return (
        <div className="space-y-6 max-w-3xl">
            <div className="bg-white border border-gold-soft/30 rounded-md p-6">
                <h3 className="label-overline text-wine mb-4">Produtor → Produtor</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <NumField label="Comissão base marketplace (%)" value={s.producer.standard_fee} onChange={(v) => setField("producer", "standard_fee", v)} testid="cfg-standard-fee" />
                    <NumField label="Comissão promocional (%)" value={s.producer.promo_fee} onChange={(v) => setField("producer", "promo_fee", v)} testid="cfg-promo-fee" />
                    <NumField label="Período de benefício (dias)" value={s.producer.benefit_days} onChange={(v) => setField("producer", "benefit_days", v)} testid="cfg-benefit-days" />
                </div>
            </div>
            <div className="bg-white border border-gold-soft/30 rounded-md p-6">
                <h3 className="label-overline text-wine mb-4">Cliente → Cliente</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <NumField label="Desconto 1ª compra amigo (%)" value={s.customer.friend_discount_pct} onChange={(v) => setField("customer", "friend_discount_pct", v)} testid="cfg-friend-discount" />
                    <NumField label="Crédito do indicador (€)" value={s.customer.referrer_credit_eur} onChange={(v) => setField("customer", "referrer_credit_eur", v)} testid="cfg-referrer-credit" />
                    <NumField label="Validade do crédito (meses)" value={s.customer.credit_validity_months} onChange={(v) => setField("customer", "credit_validity_months", v)} testid="cfg-credit-validity" />
                </div>
            </div>
            <div className="bg-white border border-gold-soft/30 rounded-md p-6">
                <h3 className="label-overline text-wine mb-4">Antifraude</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="cfg-fraud-active">
                        <input type="checkbox" checked={!!s.fraud?.active} onChange={(e) => setField("fraud", "active", e.target.checked)} className="accent-wine h-4 w-4" />
                        <span className="text-sm text-wine-dark">Deteção de fraude ativa</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="cfg-fraud-block-high">
                        <input type="checkbox" checked={!!s.fraud?.block_high_auto_reward} onChange={(e) => setField("fraud", "block_high_auto_reward", e.target.checked)} className="accent-wine h-4 w-4" />
                        <span className="text-sm text-wine-dark">Reter recompensa em risco HIGH</span>
                    </label>
                    <NumField label="Limite de velocidade (24h)" value={s.fraud?.velocity_threshold ?? 5} onChange={(v) => setField("fraud", "velocity_threshold", v)} testid="cfg-fraud-velocity" />
                </div>
            </div>
            <div className="bg-gold-soft/10 border border-gold-soft/30 rounded-md px-4 py-3 text-xs text-wine-dark/70">
                As alterações aplicam-se apenas a novas vendas — o snapshot financeiro de cada encomenda anterior é preservado. Todas as alterações ficam registadas em auditoria.
            </div>
            <button onClick={save} disabled={busy} className="btn-wine inline-flex items-center gap-2" data-testid="cfg-save">
                <Save className="h-4 w-4" /> {busy ? "A guardar…" : "Guardar configurações"}
            </button>
        </div>
    );
}

/* ---------- Afiliados Profissionais (Motor 3) ---------- */
const COMM_STATUS = { held: "Retida", pending: "Pendente", approved: "Aprovada", paid: "Paga", cancelled: "Cancelada" };
const COMM_BADGE = { held: "reserved", pending: "pending", approved: "approved", paid: "qualified", cancelled: "reversed" };

function Affiliates() {
    const [sub, setSub] = useState("affiliates");
    return (
        <div className="space-y-4">
            <div className="flex gap-1 bg-cream rounded-md p-1 w-fit">
                <button onClick={() => setSub("affiliates")} className={`inline-flex items-center gap-2 label-overline text-[10px] px-3 py-2 rounded-md transition-colors ${sub === "affiliates" ? "bg-wine text-cream" : "text-wine-dark hover:bg-wine/5"}`} data-testid="affiliate-subtab-list">
                    <Megaphone className="h-3.5 w-3.5" /> Afiliados
                </button>
                <button onClick={() => setSub("payouts")} className={`inline-flex items-center gap-2 label-overline text-[10px] px-3 py-2 rounded-md transition-colors ${sub === "payouts" ? "bg-wine text-cream" : "text-wine-dark hover:bg-wine/5"}`} data-testid="affiliate-subtab-payouts">
                    <CircleDollarSign className="h-3.5 w-3.5" /> Payouts
                </button>
            </div>
            {sub === "affiliates" ? <AffiliatesList /> : <AffiliatePayouts />}
        </div>
    );
}

function AffiliatesList() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [active, setActive] = useState("all");
    const [creating, setCreating] = useState(false);
    const [selected, setSelected] = useState(null);
    const load = () => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (active !== "all") p.set("active", active);
        return api.get(`/admin/affiliates?${p.toString()}`).then((r) => setData(r.data)).catch(() => {});
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);
    if (!data) return <Loading />;
    const prog = data.program || {};

    return (
        <div className="space-y-4" data-testid="rewards-affiliates">
            <div className="bg-gold-soft/10 border border-gold-soft/30 rounded-md px-4 py-3 text-xs text-wine-dark/70">
                A comissão do afiliado é retirada da <strong>margem Caseirinho</strong> — o produtor recebe sempre o valor integral. Atribuição por último clique dentro de {prog.cookie_days || 30} dias. Comissão base: {prog.default_commission_pct ?? 5}%.
                {!prog.active && <span className="ml-1 text-wine font-medium">· Motor de afiliados INATIVO nas configurações — sem novas conversões.</span>}
            </div>

            <div className="bg-white border border-gold-soft/30 rounded-md p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 text-wine-dark/40 ml-1" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Pesquisar nome, email ou código…" className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="affiliate-search" />
                </div>
                <select value={active} onChange={(e) => setActive(e.target.value)} className="text-xs uppercase tracking-wider bg-cream border border-wine-dark/10 rounded-md px-3 py-2" data-testid="affiliate-active-filter">
                    <option value="all">Todos</option><option value="true">Ativos</option><option value="false">Inativos</option>
                </select>
                <button onClick={() => setCreating(true)} className="btn-wine text-[10px] px-4 py-2 inline-flex items-center gap-1.5" data-testid="affiliate-new-btn">
                    <Plus className="h-3.5 w-3.5" /> Novo afiliado
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {data.affiliates.map((a) => (
                    <AffiliateCard key={a.id} a={a} onToggle={load} onOpen={() => setSelected(a.id)} />
                ))}
                {data.affiliates.length === 0 && (
                    <div className="col-span-full text-center py-12 text-wine-dark/50 bg-white border border-gold-soft/30 rounded-md" data-testid="affiliate-empty">
                        Ainda sem afiliados. Crie o primeiro para gerar um link <code>?via=</code>.
                    </div>
                )}
            </div>

            {creating && <AffiliateForm program={prog} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
            {selected && <AffiliateDrawer id={selected} onClose={() => setSelected(null)} onChanged={load} />}
        </div>
    );
}

function AffiliateCard({ a, onToggle, onOpen }) {
    const s = a.stats || {};
    const link = affiliateLink(a.code);
    async function toggle(e) {
        e.stopPropagation();
        try { await api.patch(`/admin/affiliates/${a.id}`, { active: !a.active }); toast.success(a.active ? "Afiliado desativado" : "Afiliado ativado"); onToggle(); }
        catch { toast.error("Erro ao atualizar"); }
    }
    function copy(e) {
        e.stopPropagation();
        navigator.clipboard.writeText(link).then(() => toast.success("Link copiado")).catch(() => toast.error("Não foi possível copiar"));
    }
    return (
        <div onClick={onOpen} className="bg-white border border-gold-soft/30 rounded-md p-4 hover:border-wine/40 transition-colors cursor-pointer" data-testid={`affiliate-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="font-heading text-lg text-wine-dark truncate">{a.name}</p>
                    <p className="text-xs text-wine-dark/50 truncate">{a.email || "—"}</p>
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs bg-cream border border-wine-dark/10 rounded px-2 py-0.5 font-mono text-wine">
                        <Link2 className="h-3 w-3" /> ?via={a.code}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge label={a.active ? "Ativo" : "Inativo"} status={a.active ? "qualified" : "reversed"} />
                    <div className="flex gap-1">
                        <button onClick={copy} title="Copiar link" className="p-1.5 rounded border border-wine-dark/15 text-wine-dark hover:bg-wine/5" data-testid={`affiliate-copy-${a.id}`}><Copy className="h-3.5 w-3.5" /></button>
                        <button onClick={toggle} title={a.active ? "Desativar" : "Ativar"} className={`p-1.5 rounded border ${a.active ? "border-destructive/30 text-destructive hover:bg-destructive/10" : "border-vineyard/30 text-vineyard hover:bg-vineyard/10"}`} data-testid={`affiliate-toggle-${a.id}`}><Power className="h-3.5 w-3.5" /></button>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t border-wine-dark/5">
                <MiniStat icon={MousePointerClick} label="Cliques" value={s.clicks ?? 0} />
                <MiniStat icon={CircleDollarSign} label="Conversões" value={s.conversions ?? 0} />
                <MiniStat icon={TrendingDown} label="Taxa" value={`${s.conversion_rate ?? 0}%`} />
                <MiniStat icon={Coins} label="Comissão" value={`€${(s.total_earned ?? 0).toFixed(0)}`} highlight />
            </div>
            <div className="flex flex-wrap gap-2 mt-3 text-[10px]">
                <span className="label-overline text-gold-deep bg-gold-soft/20 px-2 py-0.5 rounded">Pendente €{(s.pending ?? 0).toFixed(2)}</span>
                <span className="label-overline text-vineyard bg-vineyard/15 px-2 py-0.5 rounded">Aprovada €{(s.approved ?? 0).toFixed(2)}</span>
                <span className="label-overline text-wine bg-wine/10 px-2 py-0.5 rounded">Paga €{(s.paid ?? 0).toFixed(2)}</span>
            </div>
        </div>
    );
}

function MiniStat({ icon: Icon, label, value, highlight }) {
    return (
        <div className="text-center">
            <Icon className={`h-3.5 w-3.5 mx-auto ${highlight ? "text-wine" : "text-wine-dark/40"}`} strokeWidth={1.5} />
            <p className={`font-heading text-base mt-0.5 ${highlight ? "text-wine" : "text-wine-dark"}`}>{value}</p>
            <p className="label-overline text-[8px] text-wine-dark/50">{label}</p>
        </div>
    );
}

function AffiliateForm({ program, onClose, onSaved }) {
    const [f, setF] = useState({ name: "", email: "", code: "", commission_pct: "", cookie_days: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    async function submit() {
        if (!f.name.trim()) { toast.error("Nome obrigatório"); return; }
        setBusy(true);
        try {
            const payload = {
                name: f.name.trim(), email: f.email.trim(), code: f.code.trim(), notes: f.notes.trim(),
                commission_pct: f.commission_pct === "" ? null : parseFloat(f.commission_pct),
                cookie_days: f.cookie_days === "" ? null : parseInt(f.cookie_days, 10),
            };
            await api.post("/admin/affiliates", payload);
            toast.success("Afiliado criado");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao criar afiliado"); }
        setBusy(false);
    }
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-wine-dark/40 p-4" onClick={onClose} data-testid="affiliate-form-modal">
            <div className="bg-cream rounded-md border border-gold-soft/40 w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <h3 className="font-heading text-xl text-wine-dark">Novo afiliado profissional</h3>
                    <button onClick={onClose} className="p-1 text-wine-dark/50 hover:text-wine" data-testid="affiliate-form-close"><X className="h-5 w-5" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TextField label="Nome *" value={f.name} onChange={(v) => set("name", v)} testid="affiliate-form-name" />
                    <TextField label="Email" value={f.email} onChange={(v) => set("email", v)} testid="affiliate-form-email" />
                    <TextField label="Código ?via= (auto se vazio)" value={f.code} onChange={(v) => set("code", v)} testid="affiliate-form-code" />
                    <TextField label={`% Comissão (base ${program.default_commission_pct ?? 5}%)`} value={f.commission_pct} onChange={(v) => set("commission_pct", v)} type="number" testid="affiliate-form-pct" />
                    <TextField label={`Cookie days (base ${program.cookie_days ?? 30})`} value={f.cookie_days} onChange={(v) => set("cookie_days", v)} type="number" testid="affiliate-form-cookie" />
                </div>
                <label className="block">
                    <span className="label-overline text-wine-dark/70 text-[10px]">Notas</span>
                    <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5 w-full bg-white border border-wine-dark/15 rounded-md px-3 py-2 text-sm focus:border-wine" data-testid="affiliate-form-notes" />
                </label>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="btn-wine-outline text-xs px-4 py-2" data-testid="affiliate-form-cancel">Cancelar</button>
                    <button onClick={submit} disabled={busy} className="btn-wine text-xs px-4 py-2 inline-flex items-center gap-1.5" data-testid="affiliate-form-submit">
                        <Plus className="h-3.5 w-3.5" /> {busy ? "A criar…" : "Criar afiliado"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AffiliateDrawer({ id, onClose, onChanged }) {
    const [d, setD] = useState(null);
    const load = () => api.get(`/admin/affiliates/${id}`).then((r) => setD(r.data)).catch(() => {});
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    async function setStatus(cid, status) {
        try { await api.post(`/admin/affiliate-commissions/${cid}/status`, { status }); toast.success("Estado atualizado"); load(); onChanged && onChanged(); }
        catch { toast.error("Erro ao atualizar comissão"); }
    }
    const shareLink = d ? affiliateLink(d.affiliate.code) : "";
    function copyLink() {
        navigator.clipboard.writeText(shareLink).then(() => toast.success("Link copiado")).catch(() => {});
    }
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-wine-dark/40" onClick={onClose} data-testid="affiliate-drawer">
            <div className="bg-cream h-full w-full max-w-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {!d ? <div className="p-8"><Loading /></div> : (
                    <div className="p-6 space-y-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <span className="label-overline text-wine/60">Afiliado</span>
                                <h3 className="font-heading text-2xl text-wine-dark">{d.affiliate.name}</h3>
                                <p className="text-sm text-wine-dark/60">{d.affiliate.email || "—"}</p>
                            </div>
                            <button onClick={onClose} className="p-1.5 text-wine-dark/50 hover:text-wine" data-testid="affiliate-drawer-close"><X className="h-5 w-5" /></button>
                        </div>

                        <div className="bg-white border border-gold-soft/30 rounded-md p-3 flex items-center justify-between gap-3">
                            <code className="text-sm text-wine font-mono truncate">{shareLink}</code>
                            <button onClick={copyLink} className="btn-wine-outline text-[10px] px-3 py-1.5 inline-flex items-center gap-1.5 shrink-0" data-testid="affiliate-drawer-copy"><Copy className="h-3.5 w-3.5" /> Copiar</button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Kpi label="Cliques" value={d.stats.clicks} icon={MousePointerClick} />
                            <Kpi label="Conversões" value={d.stats.conversions} icon={CircleDollarSign} />
                            <Kpi label="Vendas geradas" value={`€${d.stats.sales.toFixed(2)}`} icon={Coins} />
                            <Kpi label="Comissão total" value={`€${d.stats.total_earned.toFixed(2)}`} icon={Coins} highlight />
                        </div>

                        <TableCard title="Comissões" testid="affiliate-commissions" head={["Encomenda", "Base", "%", "Comissão", "Margem após", "Estado", "Ações"]}>
                            {d.commissions.map((c) => (
                                <tr key={c.id} className="border-t border-wine-dark/5" data-testid={`affiliate-comm-${c.id}`}>
                                    <Td>#{(c.order_id || "").slice(0, 8)}</Td>
                                    <Td>€{(c.base_amount || 0).toFixed(2)}</Td>
                                    <Td>{c.commission_pct}%</Td>
                                    <Td className="text-vineyard font-heading">€{(c.commission_amount || 0).toFixed(2)}</Td>
                                    <Td className="text-xs">€{(c.platform_margin_after ?? 0).toFixed(2)}</Td>
                                    <Td><Badge label={COMM_STATUS[c.status] || c.status} status={COMM_BADGE[c.status]} /></Td>
                                    <Td>
                                        <div className="flex gap-1">
                                            {["pending", "held"].includes(c.status) && (
                                                <button onClick={() => setStatus(c.id, "approved")} title="Aprovar" className="p-1.5 rounded border border-vineyard/30 text-vineyard hover:bg-vineyard/10" data-testid={`comm-approve-${c.id}`}><Check className="h-3.5 w-3.5" /></button>
                                            )}
                                            {c.status === "approved" && (
                                                <button onClick={() => setStatus(c.id, "paid")} title="Marcar paga" className="p-1.5 rounded border border-wine/30 text-wine hover:bg-wine/10" data-testid={`comm-pay-${c.id}`}><Coins className="h-3.5 w-3.5" /></button>
                                            )}
                                            {c.status !== "cancelled" && c.status !== "paid" && (
                                                <button onClick={() => setStatus(c.id, "cancelled")} title="Cancelar" className="p-1.5 rounded border border-destructive/30 text-destructive hover:bg-destructive/10" data-testid={`comm-cancel-${c.id}`}><Ban className="h-3.5 w-3.5" /></button>
                                            )}
                                        </div>
                                    </Td>
                                </tr>
                            ))}
                            {d.commissions.length === 0 && <EmptyRow cols={7} />}
                        </TableCard>

                        <TableCard title="Cliques recentes" testid="affiliate-clicks" head={["Data", "Página"]}>
                            {d.clicks.map((c) => (
                                <tr key={c.id} className="border-t border-wine-dark/5">
                                    <Td className="text-xs">{fmtDate(c.created_at)}</Td>
                                    <Td className="text-xs">{c.path || "—"}</Td>
                                </tr>
                            ))}
                            {d.clicks.length === 0 && <EmptyRow cols={2} />}
                        </TableCard>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------- Payouts de Afiliados ---------- */
const PAYOUT_STATUS_PT = { draft: "Rascunho", ready: "Pronto", processing: "A processar", paid: "Pago", failed: "Falhou", cancelled: "Cancelado" };
const PAYOUT_BADGE = { draft: "pending", ready: "reserved", processing: "awaiting_sale", paid: "qualified", failed: "reversed", cancelled: "reversed" };
const PAYOUT_NEXT = {
    draft: [["ready", "Marcar pronto"]],
    ready: [["processing", "Processar"]],
    processing: [["paid", "Marcar pago"], ["failed", "Falhou"]],
    failed: [["processing", "Reprocessar"]],
};

function AffiliatePayouts() {
    const [period, setPeriod] = useState("all");
    const [summary, setSummary] = useState(null);
    const [payouts, setPayouts] = useState(null);
    const [busy, setBusy] = useState(false);
    const [detail, setDetail] = useState(null);
    const loadSummary = () => api.get(`/admin/affiliate-payouts/summary?period=${period}`).then((r) => setSummary(r.data)).catch(() => {});
    const loadPayouts = () => api.get(`/admin/affiliate-payouts`).then((r) => setPayouts(r.data)).catch(() => {});
    useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [period]);
    useEffect(() => { loadPayouts(); }, []);

    async function generate() {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/affiliate-payouts/generate", { period });
            if (data.count > 0) toast.success(`${data.count} lote(s) gerado(s)`);
            else toast.info("Sem comissões aprovadas elegíveis (≥ mínimo) para gerar lote");
            loadSummary(); loadPayouts();
        } catch { toast.error("Erro ao gerar lotes"); }
        setBusy(false);
    }
    async function setStatus(pid, status) {
        let reference;
        if (status === "paid") reference = window.prompt("Referência do pagamento (opcional):", "") ?? "";
        try {
            await api.patch(`/admin/affiliate-payouts/${pid}`, { status, reference });
            toast.success("Lote atualizado");
            loadPayouts(); loadSummary();
        } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar lote"); }
    }
    async function exportPayout(pid, format) {
        try {
            const res = await api.get(`/admin/affiliate-payouts/${pid}/export?format=${format}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = format === "xlsx" ? `payout_${pid.slice(0, 8)}.xlsx` : `payout_${pid.slice(0, 8)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Exportação concluída");
        } catch { toast.error("Erro ao exportar"); }
    }
    if (!summary || !payouts) return <Loading />;

    return (
        <div className="space-y-5" data-testid="affiliate-payouts">
            <div className="bg-white border border-gold-soft/30 rounded-md p-3 flex flex-wrap items-end gap-3">
                <label className="text-xs text-wine-dark/60">
                    Período
                    <input type="month" value={period === "all" ? "" : period} onChange={(e) => setPeriod(e.target.value || "all")} className="ml-2 border border-wine-dark/15 rounded px-2 py-1.5 text-xs" data-testid="payout-period" />
                </label>
                <button onClick={() => setPeriod("all")} className={`label-overline text-[10px] px-3 py-1.5 rounded-md border ${period === "all" ? "bg-wine text-cream border-wine" : "border-wine-dark/15 text-wine-dark hover:bg-wine/5"}`} data-testid="payout-period-all">Todos</button>
                <div className="flex-1" />
                <span className="text-xs text-wine-dark/60">Payout mínimo: <strong>€{summary.min_payout.toFixed(2)}</strong></span>
                <button onClick={generate} disabled={busy} className="btn-wine text-[10px] px-4 py-2 inline-flex items-center gap-1.5" data-testid="payout-generate-btn">
                    <Coins className="h-3.5 w-3.5" /> {busy ? "A gerar…" : "Gerar lote mensal"}
                </button>
            </div>

            <TableCard title="Resumo por afiliado" testid="payout-summary" head={["Afiliado", "Vendas qualif.", "Comissão total", "Pendente", "Aprovada", "Paga", "Disponível", "Elegível"]}>
                {summary.summary.map((s) => (
                    <tr key={s.affiliate_id} className="border-t border-wine-dark/5">
                        <Td>{s.affiliate_name}</Td>
                        <Td>€{s.qualified_sales.toFixed(2)}</Td>
                        <Td className="font-heading">€{s.total_commission.toFixed(2)}</Td>
                        <Td className="text-gold-deep">€{s.pending.toFixed(2)}</Td>
                        <Td className="text-vineyard">€{s.approved.toFixed(2)}</Td>
                        <Td className="text-wine">€{s.paid.toFixed(2)}</Td>
                        <Td className="font-heading text-vineyard">€{s.available_for_payout.toFixed(2)}</Td>
                        <Td>{s.eligible ? <Badge label="Elegível" status="qualified" /> : <span className="text-xs text-wine-dark/40">—</span>}</Td>
                    </tr>
                ))}
                {summary.summary.length === 0 && <EmptyRow cols={8} />}
            </TableCard>

            <TableCard title={`Lotes de pagamento (${payouts.totals.count})`} testid="payout-batches" head={["Lote", "Afiliado", "Período", "Comissões", "Valor", "Estado", "Referência", "Ações"]}>
                {payouts.payouts.map((p) => (
                    <tr key={p.id} className="border-t border-wine-dark/5" data-testid={`payout-row-${p.id}`}>
                        <Td className="font-mono text-xs">#{p.id.slice(0, 8)}</Td>
                        <Td>{p.affiliate_name}</Td>
                        <Td className="text-xs">{p.period}</Td>
                        <Td>{p.commission_count}</Td>
                        <Td className="font-heading">€{(p.gross_amount || 0).toFixed(2)}</Td>
                        <Td><Badge label={PAYOUT_STATUS_PT[p.status] || p.status} status={PAYOUT_BADGE[p.status]} /></Td>
                        <Td className="text-xs">{p.reference || "—"}</Td>
                        <Td>
                            <div className="flex flex-wrap gap-1">
                                {(PAYOUT_NEXT[p.status] || []).map(([st, label]) => (
                                    <button key={st} onClick={() => setStatus(p.id, st)} className="btn-wine-outline text-[9px] px-2 py-1" data-testid={`payout-${st}-${p.id}`}>{label}</button>
                                ))}
                                {!["paid", "cancelled"].includes(p.status) && (
                                    <button onClick={() => setStatus(p.id, "cancelled")} title="Cancelar" className="p-1.5 rounded border border-destructive/30 text-destructive hover:bg-destructive/10" data-testid={`payout-cancel-${p.id}`}><Ban className="h-3 w-3" /></button>
                                )}
                                <button onClick={() => setDetail(p.id)} title="Ver detalhe" className="p-1.5 rounded border border-wine-dark/15 text-wine-dark hover:bg-wine/5" data-testid={`payout-view-${p.id}`}><Eye className="h-3 w-3" /></button>
                                <button onClick={() => exportPayout(p.id, "csv")} title="CSV" className="p-1.5 rounded border border-wine-dark/15 text-wine-dark hover:bg-wine/5" data-testid={`payout-csv-${p.id}`}><Download className="h-3 w-3" /></button>
                                <button onClick={() => exportPayout(p.id, "xlsx")} title="Excel" className="p-1.5 rounded border border-wine/30 text-wine hover:bg-wine/10" data-testid={`payout-xlsx-${p.id}`}><Download className="h-3 w-3" /></button>
                            </div>
                        </Td>
                    </tr>
                ))}
                {payouts.payouts.length === 0 && <EmptyRow cols={8} />}
            </TableCard>
            <p className="text-xs text-wine-dark/50">Um lote só inclui comissões <strong>aprovadas</strong> ainda não pagas e acima do mínimo. Marcar como <strong>Pago</strong> atualiza as comissões para "paga" com referência e regista auditoria (idempotente). Cancelar liberta as comissões não pagas para um novo lote. Sem pagamentos reais nesta fase.</p>

            {detail && <PayoutDrawer id={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function PayoutDrawer({ id, onClose }) {
    const [d, setD] = useState(null);
    useEffect(() => { api.get(`/admin/affiliate-payouts/${id}`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-wine-dark/40" onClick={onClose} data-testid="payout-drawer">
            <div className="bg-cream h-full w-full max-w-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {!d ? <div className="p-8"><Loading /></div> : (
                    <div className="p-6 space-y-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <span className="label-overline text-wine/60">Lote de pagamento</span>
                                <h3 className="font-heading text-2xl text-wine-dark">{d.payout.affiliate_name}</h3>
                                <p className="text-sm text-wine-dark/60">#{d.payout.id.slice(0, 8)} · {d.payout.period} · <Badge label={PAYOUT_STATUS_PT[d.payout.status] || d.payout.status} status={PAYOUT_BADGE[d.payout.status]} /></p>
                            </div>
                            <button onClick={onClose} className="p-1.5 text-wine-dark/50 hover:text-wine" data-testid="payout-drawer-close"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <Kpi label="Valor bruto" value={`€${(d.payout.gross_amount || 0).toFixed(2)}`} icon={Coins} highlight />
                            <Kpi label="Comissões" value={d.payout.commission_count} icon={CircleDollarSign} />
                            <Kpi label="Referência" value={d.payout.reference || "—"} icon={Link2} />
                        </div>
                        <TableCard title="Comissões do lote" testid="payout-drawer-commissions" head={["Encomenda", "Base", "%", "Comissão", "Estado", "Data"]}>
                            {d.commissions.map((c) => (
                                <tr key={c.id} className="border-t border-wine-dark/5">
                                    <Td>#{(c.order_id || "").slice(0, 8)}</Td>
                                    <Td>€{(c.base_amount || 0).toFixed(2)}</Td>
                                    <Td>{c.commission_pct}%</Td>
                                    <Td className="text-vineyard font-heading">€{(c.commission_amount || 0).toFixed(2)}</Td>
                                    <Td><Badge label={COMM_STATUS[c.status] || c.status} status={COMM_BADGE[c.status]} /></Td>
                                    <Td className="text-xs">{fmtDate(c.created_at)}</Td>
                                </tr>
                            ))}
                            {d.commissions.length === 0 && <EmptyRow cols={6} />}
                        </TableCard>
                        {d.payout.status_history?.length > 0 && (
                            <div className="bg-white border border-gold-soft/30 rounded-md p-4">
                                <h4 className="label-overline text-wine mb-2">Histórico de estados</h4>
                                <ul className="space-y-1 text-xs text-wine-dark/70">
                                    {d.payout.status_history.map((h, i) => (
                                        <li key={i} className="flex justify-between">
                                            <span>{PAYOUT_STATUS_PT[h.status] || h.status} · {h.by_name || "—"}</span>
                                            <span className="text-wine-dark/40">{fmtDate(h.at)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function TextField({ label, value, onChange, type = "text", testid }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark/70 text-[10px]">{label}</span>
            <input type={type} step={type === "number" ? "0.01" : undefined} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full bg-white border border-wine-dark/15 rounded-md px-3 py-2.5 text-sm focus:border-wine" data-testid={testid} />
        </label>
    );
}

/* ---------- shared ---------- */
function Loading() { return <p className="text-wine-dark/50 label-overline py-8">A carregar…</p>; }
function Td({ children, className = "" }) { return <td className={`py-3 px-4 text-sm text-wine-dark ${className}`}>{children}</td>; }
function EmptyRow({ cols }) { return <tr><td colSpan={cols} className="text-center py-10 text-wine-dark/50">Sem registos.</td></tr>; }

function TableCard({ title, head, children, testid }) {
    return (
        <div className="bg-white border border-gold-soft/30 rounded-md overflow-hidden" data-testid={testid}>
            {title && <div className="px-5 py-4 border-b border-wine-dark/10"><h3 className="font-heading text-lg text-wine-dark">{title}</h3></div>}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10 bg-cream/40">
                            {head.map((h) => <th key={h} className="py-3 px-4">{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>{children}</tbody>
                </table>
            </div>
        </div>
    );
}

function Badge({ label, status }) {
    const map = {
        qualified: "bg-vineyard text-cream", available: "bg-vineyard/15 text-vineyard",
        pending: "bg-gold-soft/20 text-gold-deep", awaiting_sale: "bg-wine/10 text-wine",
        reversed: "bg-destructive/15 text-destructive", expired: "bg-wine-dark/10 text-wine-dark/60",
        used: "bg-wine/10 text-wine", reserved: "bg-gold-soft/20 text-gold-deep",
        approved: "bg-vineyard/15 text-vineyard", rejected: "bg-destructive/15 text-destructive",
    };
    return <span className={`inline-flex items-center label-overline px-2 py-1 rounded text-[10px] ${map[status] || "bg-wine-dark/10 text-wine-dark/60"}`}>{label}</span>;
}

function NumField({ label, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="label-overline text-wine-dark/70 text-[10px]">{label}</span>
            <input
                type="number" step="0.01" value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="mt-1.5 w-full bg-cream border border-wine-dark/15 rounded-md px-3 py-2.5 text-sm focus:border-wine"
                data-testid={testid}
            />
        </label>
    );
}
